import { useCallback, useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useProperty, useSearch, useSiteContext, useTranslation } from '@eti/providers';
import { routes } from '../../../constants/routesAndApisConstants';
import { getSearchParams, transformSearchParams } from '../utils/utils';
import RedirectFromBrandToBrand from './RedirectFromBrandToBrand';

const SearchRedirection = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const { p } = useProperty();
  const { brand } = useSiteContext();
  const { search } = useLocation();
  const { currentSearchData, setSearchData } = useSearch();
  const newBrandCode = (p('IbeClient.RedirectCustomerToAnotherSite.SiteName') ?? '').split('_')[0];

  const queryParams = useMemo(() => new URLSearchParams(search), [search]);

  const newBrandUrl = useMemo(() => {
    if (!currentSearchData || !currentSearchData.origin || !currentSearchData.routes.length) {
      return '';
    }
    const redirectionUrl = p('IbeClient.RedirectCustomerToAnotherSite.SiteUrl');
    const searchParams = getSearchParams(currentSearchData, brand.code);

    return `https://${redirectionUrl}${window.spa_variables.basePath}${routes.NEW_BRAND_REDIRECTION}?${searchParams}&domain=closed-brand-referral`;
  }, [brand.code, p, currentSearchData]);

  const redirectToNewBrand = useCallback(() => {
    window.location.href = newBrandUrl;
  }, [newBrandUrl]);

  useEffect(() => {
    let timeout;

    if (currentSearchData.origin) {
      const redirectionTimeInMills = p('IbeClient.RedirectCustomerToAnotherSite.Duration') * 1000;
      timeout = setTimeout(redirectToNewBrand, redirectionTimeInMills);
    } else {
      const travelerTexts = {
        adult: t('Adult'),
        adults: t('Adults'),
        child: t('Child'),
        children: t('Children'),
      };
      const searchParameters = transformSearchParams(queryParams, travelerTexts);
      setSearchData({
        cabinClass: searchParameters.cabinClass,
        date: {
          departure: searchParameters.startDate,
          return: searchParameters.endDate,
        },
        destination: {
          city: searchParameters.destination,
        },
        origin: {
          city: searchParameters.origin,
        },
        routes: searchParameters.routes,
        shouldOnlyShowDirectFlights: searchParameters.direct,
        travelers: {
          adults: searchParameters.adults,
          childAges: searchParameters.childAges,
          summary: searchParameters.travelers,
        },
        tripType: searchParameters.tripType,
      });
      history.push(routes.RESULT);
    }
    return () => clearTimeout(timeout);
  }, [
    brand.code,
    history,
    p,
    queryParams,
    redirectToNewBrand,
    search,
    currentSearchData,
    setSearchData,
    t,
  ]);

  if (!currentSearchData.origin) {
    return null;
  }

  return (
    <RedirectFromBrandToBrand
      dataTestId="search-redirection"
      fromBrandCode={brand.code}
      subtitle={t('IbeClient.RedirectCustomerToAnotherSite.Text')}
      title={t('IbeClient.RedirectCustomerToAnotherSite.Header')}
      toBrandCode={newBrandCode}
      toBrandUrl={newBrandUrl}
    />
  );
};

export default SearchRedirection;
